import { LitElement, html, TemplateResult, PropertyValues, nothing } from 'lit';
import { property, state, query } from 'lit/decorators.js';
import { ListEvents, ListItem, SelectedOption } from '../types';
import { styles } from '../view/styles';
import { WEB_COMPONENT_GZ_INPUT_SEARCH } from '../../../webcomponents.constants';
import { GzInputSearchWebComponent } from '../../gzInput';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { desanitizeObject } from '../../../utils/sanitization.utils';
import { ElementClass, elementClassListAdd, elementClassListRemove } from '../../../utils/htmlElement.utils';
import { classMap } from 'lit/directives/class-map.js';
import { debounce } from '../../../utils/event.utils';
import { groupBy } from 'lodash';
import { FontSizes, FontWeights } from 'apps/webcomponents/src/types/common/types';
import { emphasizeString } from '../../../utils/htmlElement.utils';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

export class GzSingleSelectListWebComponent extends LitElement {
  private static optionsListMaxLength = 6;
  private static groupKey = 'parent';

  @property({ type: Array, attribute: true, reflect: true })
  filterOptions: ListItem[] = [];

  @property({ type: String, attribute: true, reflect: true })
  searchPlaceholderString = '';

  @property({ type: String, attribute: true, reflect: true })
  noSearchResultsString = '';

  /**
   * If set to TRUE, the component will only emit a `searchInputChange` event containing the search string instead of
   * performing client side list filtering. This can be used when the list items filtering is performed server-side.
   */
  @property({ type: Boolean, attribute: true, reflect: true })
  hasDynamicSearch = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  isLoading = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  shouldHighlightSearchStrings = false;

  @query('#selectList')
  private readonly selectList!: HTMLElement;

  @state() private hasGroupedOptions = false;

  /**
   * This internal reactive property helps re-render the list items: when TRUE, the list items need to be rendered as ungrouped.
   * When FALSE, the list items are rendered as grouped.
   */
  @state()
  protected listIsFiltered = false;

  protected optionsList!: NodeList;

  @query(WEB_COMPONENT_GZ_INPUT_SEARCH)
  public searchInput!: GzInputSearchWebComponent | null;

  /**
   * This property will store the selected value.
   */
  @state()
  protected _selectedOption: SelectedOption | null = null;

  /**
   * This property is used to keep track of whether the selection event has been emitted.
   */
  @state()
  selectionEventEmitted = false;

  @state()
  searchString = '';

  public set selectedOption(option: SelectedOption | null) {
    this._selectedOption = option;
  }

  public get selectedOption(): SelectedOption | null {
    return this._selectedOption;
  }

  private get allHiddenOptions(): boolean {
    return this.filterOptions.every(option => option.hidden);
  }

  static get styles() {
    return styles;
  }

  updated(changedProperties: PropertyValues) {
    if (changedProperties.has('filterOptions')) {
      this.updateComplete.then(() => {
        if (this.hasDynamicSearch && this.searchInput && !this.isLoading) {
          this.searchInput.inputFocus();
        }
      });

      if (!this.shadowRoot) {
        throw Error('Unable to render filter!');
      }

      this.optionsList = this.shadowRoot.querySelectorAll('.option-selectable');
    }
  }

  public firstUpdated(changedProperties: PropertyValues): void {
    super.firstUpdated(changedProperties);
    this.hasGroupedOptions = this.getHasGroupedOptions();
  }

  private getHasGroupedOptions(): boolean {
    return this.filterOptions.every(element => element.hasOwnProperty(GzSingleSelectListWebComponent.groupKey));
  }

  render(): TemplateResult {
    const classes = {
      list: true,
      'has-search': this.hasSearchInput(),
    };

    return html`
      <div class="${classMap(classes)}">
        <div class="list--search">${this.renderSearchInput()}</div>
        ${this.isLoading ? this.renderLoadingTemplate() : this.renderListOptions()}
      </div>
    `;
  }

  private renderLoadingTemplate(): TemplateResult {
    return html` <div class="spinner">
      <gz-progress-spinner spinnerwidth="small"></gz-progress-spinner>
    </div>`;
  }

  protected renderSearchInput(): TemplateResult {
    return html`<gz-input-search
      placeholder="${this.searchPlaceholderString}"
      inputWidth="medium"
      @blur="${e => e.stopPropagation()}"
      @change="${debounce(event => this.handleOptionsSearch(event), 700)}"
    ></gz-input-search>`;
  }

  protected renderListOptions(): TemplateResult {
    return html` <div id="selectList" class="list--options" @scroll="${this.emitListScrollEvent}">
      ${this.renderNoResultsTemplate()} ${this.renderFilterOptionsList()}
    </div>`;
  }

  protected renderNoResultsTemplate(): TemplateResult {
    const classes = {
      'list--no-results': true,
      'fs-14': true,
      visible: this.allHiddenOptions,
    };
    return html`<div id="emptyTemplate" class="${classMap(classes)}">${this.noSearchResultsString}</div>`;
  }

  protected renderFilterOptionsList(): TemplateResult | TemplateResult[] {
    if (isEmpty(this.filterOptions)) {
      return html``;
    }

    return this.hasGroupedOptions && !this.listIsFiltered ? this.renderGroupedOptions() : this.renderUngroupedOptions();
  }

  private renderUngroupedOptions(): TemplateResult {
    return html`${this.filterOptions.map(option => this.renderFilterOption(option))}`;
  }

  private renderGroupedOptions(): TemplateResult[] {
    const groupedOptions = groupBy(this.filterOptions, GzSingleSelectListWebComponent.groupKey);

    return Object.entries(groupedOptions).map(([groupTitle, options], index) =>
      this.renderFilterOptionsGroup(groupTitle, options, index)
    );
  }

  private setListIsFiltered(isFiltered: boolean): void {
    this.listIsFiltered = isFiltered;
  }

  private renderFilterOptionsGroup(groupTitle: string, options: ListItem[], groupIndex: number): TemplateResult {
    return this.renderItemsInGroup(groupTitle, options);
  }

  private renderGroupTitle(groupTitle: string): TemplateResult | string {
    if (!groupTitle || groupTitle === 'undefined') {
      return '';
    }

    return html`<gz-title
      fontSize="${FontSizes.PARAGRAPH_11}"
      fontWeight="${FontWeights.SEMIBOLD}"
      class="list-group--title"
      >${groupTitle.toUpperCase()}</gz-title
    >`;
  }

  private renderOptionsList(options: ListItem[]): TemplateResult {
    return html`<div class="list-group--list">${options.map(option => this.renderFilterOption(option))}</div>`;
  }

  private renderItemsInGroup(groupTitle: string, options: ListItem[]): TemplateResult {
    const title = this.renderGroupTitle(groupTitle);
    const content = this.renderOptionsList(options);

    return html`<div class="list-group" part="listGroup">${title}${content}</div>`;
  }

  private renderFilterOption(option: ListItem): TemplateResult {
    option = desanitizeObject(option);

    const classes = {
      'option-item': true,
      'fs-14': true,
      'option-selectable': true,
      active: `${this.getActiveOptionClass(option)}`,
      hidden: option.hidden ?? false,
    };

    const displayedText = this.shouldHighlightSearchStrings ? this.decoratedTextOnSearch(option.label) : option.label;

    return html`
      <div
        ?disabled="${option.disabled}"
        class="${classMap(classes)}"
        @click="${e => this.handleOptionChange(option, e)}"
      >
        <gz-tooltip tooltiptext="${option.label}" isellipsis="true" class="tooltip">
          <span> ${displayedText} </span>
        </gz-tooltip>
        ${option.itemSuffixRenderer
          ? html` <span class="suffix-content"> ${option.itemSuffixRenderer(option)} </span> `
          : nothing}
      </div>
    `;
  }

  private decoratedTextOnSearch(text: string) {
    return unsafeHTML(`${emphasizeString(this.searchString, text)}`);
  }

  protected handleOptionChange(option: ListItem, e: CustomEvent): void {
    if (option.disabled) {
      e.preventDefault();
      return;
    }
    e.stopPropagation();
    this.selectedOption = option;
    this.clearSearchInput(e);
    if (!this.selectionEventEmitted) {
      this.emitConfirmSelectionEvent();
      this.selectionEventEmitted = true;
    }
  }

  protected handleOptionsSearch(e: CustomEvent): void {
    const searchString = e.detail.sourceEvent;
    this.searchString = searchString;
    this.setListIsFiltered(!isEmpty(searchString));
    this.hasDynamicSearch
      ? this.emitSearchInputChangeEvent(searchString)
      : this.filterOptionsBySearchString(searchString);
  }

  private filterOptionsBySearchString(searchString: string): void {
    searchString = searchString.toLowerCase();

    const searchResult = this.getFilteredResults(searchString);

    // emit an event that notifies the parent if the list search returned any results.
    this.emitFilterOptionsEvent(searchResult);
    this.updateFilteredOptionsBySearchResult(searchResult);
  }

  private emitFilterOptionsEvent(searchResults: ListItem[]): void {
    const event = new CustomEvent(ListEvents.OPTIONS_FILTERED, {
      detail: { resultsFound: !isEmpty(searchResults), searchResults: searchResults, value: this.searchString },
      composed: true,
    });
    this.dispatchEvent(event);
  }

  protected updateFilteredOptionsBySearchResult(searchResult: ListItem[]): void {
    const newOptions = [...this.filterOptions];

    this.filterOptions.forEach((option: ListItem, index: number) => {
      const item = this.optionsList[index] as HTMLElement;
      if (searchResult.includes(option)) {
        elementClassListRemove(item, ElementClass.HIDDEN);
        newOptions[index].hidden = false;
      } else {
        elementClassListAdd(item, ElementClass.HIDDEN);
        newOptions[index].hidden = true;
      }
    });

    this.filterOptions = [...newOptions];
  }

  protected getFilteredResults(searchString: string): ListItem[] {
    return this.filterOptions.filter((option: ListItem) => {
      if (searchString.charAt(0) === ' ') {
        return;
      }

      return option.label?.toLowerCase().includes(searchString);
    });
  }

  protected hasSearchInput(): boolean {
    return this.hasDynamicSearch || this.filterOptions.length > GzSingleSelectListWebComponent.optionsListMaxLength;
  }

  public emitConfirmSelectionEvent(): void {
    const event = new CustomEvent(ListEvents.SELECTION_CHANGE, {
      detail: {
        selectedValue: this._selectedOption ? this._selectedOption.value : null,
        selectedLabel: this._selectedOption ? this._selectedOption.label : null,
        extraDetails: this._selectedOption?.extraDetails,
      },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(event);
  }

  public emitListScrollEvent(): void {
    const { scrollTop, scrollHeight, offsetHeight, clientHeight } = this.selectList;
    this.dispatchEvent(
      new CustomEvent(ListEvents.SCROLL, {
        detail: {
          scrollTop,
          scrollHeight,
          offsetHeight,
          clientHeight,
        },
        composed: true,
      })
    );
  }

  protected getActiveOptionClass(option: SelectedOption): string {
    return this._selectedOption && this._selectedOption.value === option.value ? 'active' : '';
  }

  public clearSearchInput(e: Event): void {
    if (this.searchInput && this.searchInput.value) {
      this.searchInput.clearInputValue(e);
    }
  }

  private emitSearchInputChangeEvent(searchString: string): void {
    this.searchString = searchString;
    const event = new CustomEvent(ListEvents.SEARCH_INPUT_CHANGE, {
      detail: {
        value: searchString,
      },
      composed: true,
    });
    this.dispatchEvent(event);
  }

  public setListScrollPosition(scrollPosition: number): void {
    this.selectList.scrollTop = scrollPosition;
  }
}
